import { addVehicle } from "action/garage/addVehicle";
import { getProductPricesApi } from "api/category/get-product-prices";
import { IPageContent } from "api/interfaces/static-content.type";
import classNames from "classnames";
import { getUpdatedProductPrices } from "component/productHelper";
import VehicleSelectorModal from "component/vehicle-form/VehicleSelectorModal";
import { FILTER_TYPE } from "constant/product-filter.action";
import { RootState } from "interfaces/generics.type";
import BusinessTabs from "modules/b2b/business-tabs/BusinessTabs";
import PageStructureData from "modules/product/PageStructureData";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import QS from "query-string";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { isBusinessHP } from "util/helper/helper";
import { isMerchandiseCategory } from "util/product/apparel";
import SearchContactUs from "component/search/search-contact-us";
import useSafeRouter from "../../hoc/useSafeRouter";
import useScreenSize from "../../hoc/useScreenSize";
import PageContainer from "../../modules/components/PageContainer";
import RelatedCategories from "../../modules/components/related-categories/RelatedCategories";
import { getVehicleName } from "../../util/helper/getVehicleName";
import { TIER_MAKE_MODEL_PAGES, TIER_PAGES } from "../../util/product/product-constants";
import { getFragmentParams } from "../../util/url/getFragmentParams";
import { getHrefAndPath } from "../../util/url/getHrefAndPath";
import { URL_PARAM_ORDERS } from "../../util/url/urlHelper";
import { validateVehicle } from "../../util/vehicle/validate-vehicle";
import ArticleGuide from "../guide-articles/ArticleGuide";
import { getTotalFilters } from "../product/product-container-util";
import MakeModeTierInterLinking from "./MakeModeTierInterLinking";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "./PagePlaceholder";
import B2BCategoryVehicleBanner from "./category-vehicle-banner-section/B2BCategoryVehicleBanner";
import styles from "./styles.module.scss";

const SsrProductContainerCompact = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-compact" */"./SsrProductContainerCompact"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const SsrProductContainerRegular = dynamic(
  () => import(/* webpackChunkName: "ssr-product-container-regular" */"./SsrProductContainerRegular"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.CATEGORY_PAGE} /> },
);
const HeaderAutoComplete = dynamic(() => import(/* webpackChunkName: "header-autocomplete" */"component/header/header-autocomplete.component"));

interface IProps {
  staticContent: IPageContent;
}

function SsrProductContainer(props: IProps) {
  const {
    productList, filterOptions, nbcFilterableAttributes, immutableFilters, hideQsParams, url, bannerData,
    breadcrumbData, pageType, tiersWithUrls, staticContent,
    makeModelTiers, breadCrumbsV2, comonTop, innerQuery, categoryAggregation,
    isShopByCategory, categories, fitmentOptionsDetails,
    fitmentOptionsPrompt,
  } = props;

  const { query } = useSafeRouter();
  const [isMobile] = useScreenSize();
  const { t } = useTranslation("common");
  const { urlInformation } = useSelector((state: RootState) => state.utils);
  const { selectedVehicle } = useSelector((state: RootState) => state.garage);
  const { businessCustomer, loggedIn, refreshStatus } = useSelector((store: RootState) => store.authenticationState);
  const router = useRouter();
  const { asPath } = router;
  const isBusinessPage = isBusinessHP(asPath);
  const dispatch = useDispatch();
  const [updatedProductList, setUpdatedProductList] = useState(productList);
  // const [pageDisplayableTitle, setPageDisplayableTitle] = useState("");
  const appliedFilters = getTotalFilters(t, query, urlInformation, selectedVehicle, innerQuery);

  useEffect(() => {
    if (isShopByCategory) {
      return;
    }
    const { pageNumber, availabilities } = query;
    // show Out of Stock products when there are no prduct in stock.

    if (productList.products.length === 0) {
      if ((!pageNumber && !availabilities)
        || (pageNumber === 1 && !availabilities.includes("Out of Stock"))
      ) {
        const buildQS = QS.stringify({
          ...query,
          pathname: undefined,
          // scroll: undefined,
          [FILTER_TYPE.AVAILABILITY]: [t("In Stock"), t("Out of Stock")],
        }, {
          sort: (a: any, b: any) => URL_PARAM_ORDERS.indexOf(a) - URL_PARAM_ORDERS.indexOf(b),
        });
        const { href, asPath } = getHrefAndPath({ queryString: buildQS, href: query.pathname, asPath: query.pathname });
        router.replace(href, asPath);
      }
    }
  }, [productList.products]);

  useEffect(() => {
    if (!businessCustomer || isShopByCategory) {
      setUpdatedProductList(productList);
      return;
    }

    const skus = productList.products.map((product) => product.sku);
    getProductPricesApi(skus)
      .then(({ data }) => {
        const productsWithNewPrice = getUpdatedProductPrices(data, productList.products);
        setUpdatedProductList({ ...productList, products: productsWithNewPrice });
      });
  }, [businessCustomer, productList.products]);

  // Route to login page if on business page and not logged in
  useEffect(() => {
    if (!loggedIn && refreshStatus === "complete" && isBusinessPage) {
      router.replace("/login");
    }
  }, [loggedIn, refreshStatus, isBusinessPage]);

  useEffect(() => {
    if (loggedIn && businessCustomer !== null && businessCustomer === false && isBusinessPage) {
      router.replace("/");
    }
  }, [businessCustomer, router.asPath]);

  const { year: fragmentYear, make: fragmentMake, model: fragmentModel } = getFragmentParams();
  // appliedFilters.sizes = "205/55R16";

  // PageDisplayableTitle in useEffect to fix hydration error
  const getPageDisplayableTitle = () => {
    let newPageDisplayableTitle = staticContent?.pageDetails?.pageDisplayableTitle || "";

    if (isMerchandiseCategory(urlInformation)) {

    } else if (TIER_MAKE_MODEL_PAGES.includes(pageType) && (appliedFilters.year || fragmentYear)) {
      newPageDisplayableTitle = `${appliedFilters.year || fragmentYear} ${newPageDisplayableTitle}`;
    } else if (TIER_PAGES.includes(pageType) && validateVehicle(appliedFilters)) {
      const vehicleName = getVehicleName(appliedFilters, ["year", "make", "model"]);
      newPageDisplayableTitle = `${vehicleName} ${newPageDisplayableTitle}`;
    } else if (TIER_PAGES.includes(pageType) && fragmentYear && fragmentMake && fragmentModel) {
      const vehicleName = `${fragmentYear} ${fragmentMake} ${fragmentModel}`;
      newPageDisplayableTitle = `${vehicleName} ${newPageDisplayableTitle}`;
    }

    return newPageDisplayableTitle;
  };

  const pageDisplayableTitle = getPageDisplayableTitle();
  const fitmentTitle = selectedVehicle?.fitmentOptions.map((option) => option.optionValue).filter(Boolean).join(" ");

  const propsToPass = {
    productList: updatedProductList,
    filterOptions,
    nbcFilterableAttributes,
    immutableFilters,
    hideQsParams,
    url,
    bannerData,
    breadcrumbData,
    pageType,
    tiersWithUrls,
    appliedFilters,
    staticContent,
    breadCrumbsV2,
    pageDisplayableTitle,
    fitmentTitle,
    categoryAggregation,
    isShopByCategory,
    categories,
    fitmentOptionsDetails,
    fitmentOptionsPrompt,
  };

  let content;
  const common = (
    <>
      {/* used for tires size page */}
      {comonTop}

      <ArticleGuide {...{ articles: staticContent?.links?.articles }} tileWidth={240} />

      {/* https://dev.partsavatar.ca/performance-exhaust-systems#related-categories */}
      <RelatedCategories relatedCategories={staticContent?.links?.relatedCategories} />

      {/* https://dev.partsavatar.ca/exhaust-kits#popular-models */}
      <MakeModeTierInterLinking {...{ makeModelTiers, pageDetails: staticContent.pageDetails }} />

      <SearchContactUs />
    </>
  );

  const b2bCommon = (
    <>
      <BusinessTabs />
      {validateVehicle(selectedVehicle) ? <B2BCategoryVehicleBanner />
        : (
          <HideIf show={isBusinessPage}>
            <VehicleSelectorModal
              noModal
              onAddVehicle={(vehicle) => dispatch(addVehicle(vehicle, true))}
            />
          </HideIf>
        )}
      <HideIf show={isBusinessPage}>
        <div className={styles.header_autocomplete}>
          <HeaderAutoComplete
            className={styles.search_container}
            searchInputClassName={styles.search_input}
          />
        </div>
      </HideIf>
    </>
  );

  if (isMobile) {
    content = (
      <PageContainer
        className={classNames(styles.product_container_search)}
        fullWidth
      >
        {businessCustomer && b2bCommon}

        <SsrProductContainerCompact {...propsToPass} />
        {!isBusinessPage && common}
      </PageContainer>
    );
  } else {
    content = (
      <PageContainer
        className={classNames(styles.product_container_search, "row-5-padding")}
        style={{ minHeight: "100%" }}
        fullWidth
      >

        {businessCustomer && b2bCommon}
        <HideIf show={isBusinessPage}>
          <Head>
            <meta name="robots" content="noindex, nofollow" />
          </Head>
        </HideIf>
        <SsrProductContainerRegular
          {...propsToPass}
          pageStructureData={(
            <PageStructureData
              pageStructure={staticContent.pageStructure}
            />
          )}
        />
        {!isBusinessPage && common}
      </PageContainer>
    );
  }

  return content;
}

export default SsrProductContainer;
